import React from 'react';
import type { Editor } from '@tiptap/react';
import { 
  Bold, 
  Italic, 
  Strikethrough,
  Highlighter,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Link2,
  Code,
  SquareDashed,
} from 'lucide-react';
import { useCanvasStore } from '@/stores/canvasStore';
import { RailButton, RailDivider } from './RailButton';

interface TextFormattingSidebarProps {
  editor: Editor | null;
}

export function TextFormattingSidebar({ editor }: TextFormattingSidebarProps) {
  // The card being edited, for its background (text cards only)
  const card = useCanvasStore((s) => {
    const id = s.activeEditorItemId;
    return id ? s.items.find((i) => i.id === id && i.item_type === 'text_card') ?? null : null;
  });
  const updateItem = useCanvasStore((s) => s.updateItem);

  if (!editor) return null;

  const transparent = !!(card?.content as { transparent?: boolean } | null)?.transparent;

  const formatButtons = [
    {
      icon: Bold,
      label: 'Bold',
      shortcut: '⌘B',
      onClick: () => editor.chain().focus().toggleBold().run(),
      isActive: editor.isActive('bold'),
    },
    {
      icon: Italic,
      label: 'Italic',
      shortcut: '⌘I',
      onClick: () => editor.chain().focus().toggleItalic().run(),
      isActive: editor.isActive('italic'),
    },
    {
      icon: Strikethrough,
      label: 'Strikethrough',
      shortcut: '⌘⇧S',
      onClick: () => editor.chain().focus().toggleStrike().run(),
      isActive: editor.isActive('strike'),
    },
    {
      icon: Highlighter,
      label: 'Highlight',
      shortcut: '⌘⇧H',
      onClick: () => editor.chain().focus().toggleHighlight().run(),
      isActive: editor.isActive('highlight'),
    },
  ];

  const headingButtons = [
    {
      icon: Heading1,
      label: 'Heading 1',
      shortcut: '⌘⌥1',
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: editor.isActive('heading', { level: 1 }),
    },
    {
      icon: Heading2,
      label: 'Heading 2',
      shortcut: '⌘⌥2',
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: editor.isActive('heading', { level: 2 }),
    },
    {
      icon: Heading3,
      label: 'Heading 3',
      shortcut: '⌘⌥3',
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: editor.isActive('heading', { level: 3 }),
    },
  ];

  const listButtons = [
    {
      icon: List,
      label: 'Bullet List',
      shortcut: '⌘⇧8',
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      isActive: editor.isActive('bulletList'),
    },
    {
      icon: ListOrdered,
      label: 'Numbered List',
      shortcut: '⌘⇧7',
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: editor.isActive('orderedList'),
    },
    {
      icon: CheckSquare,
      label: 'Checklist',
      onClick: () => editor.chain().focus().toggleTaskList().run(),
      isActive: editor.isActive('taskList'),
    },
  ];

  const otherButtons = [
    {
      icon: Link2,
      label: 'Link',
      shortcut: '⌘K',
      onClick: () => {
        const url = window.prompt('Enter URL:');
        if (url) {
          editor.chain().focus().setLink({ href: url }).run();
        }
      },
      isActive: editor.isActive('link'),
    },
    {
      icon: Code,
      label: 'Code',
      shortcut: '⌘E',
      onClick: () => editor.chain().focus().toggleCode().run(),
      isActive: editor.isActive('code'),
    },
  ];

  // Milanote-style floating text: no fill, border or shadow, just the words on the canvas
  const cardButtons = card
    ? [
        {
          icon: SquareDashed,
          label: 'No background',
          onClick: () => updateItem(card.id, { content: { transparent: !transparent } }),
          isActive: transparent,
        },
      ]
    : [];

  const groups = [formatButtons, headingButtons, listButtons, otherButtons, cardButtons].filter((g) => g.length);

  return (
    <div className="flex flex-col items-center animate-fade-in">
      {groups.map((group, i) => (
        <React.Fragment key={i}>
          {i > 0 && <RailDivider />}
          <div className="flex flex-col items-center gap-0.5">
            {group.map(({ icon, label, shortcut, onClick, isActive }) => (
              <RailButton key={label} icon={icon} label={label} shortcut={shortcut} onClick={onClick} isActive={isActive} />
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
